
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function RelaxationGuidance() {
  const [activeTab, setActiveTab] = useState('breathing');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimer, setCurrentTimer] = useState(0);

  const techniques = [
    {
      id: 'breathing',
      name: '呼吸法',
      icon: 'ri-lungs-line',
      description: '深呼吸によるリラクゼーション'
    },
    {
      id: 'meditation',
      name: '瞑想',
      icon: 'ri-brain-line',
      description: 'マインドフルネス瞑想'
    },
    {
      id: 'muscle',
      name: '筋弛緩法',
      icon: 'ri-body-scan-line',
      description: '段階的筋弛緩法'
    },
    {
      id: 'imagery',
      name: 'イメージ法',
      icon: 'ri-landscape-line',
      description: 'ガイド付きイメージング'
    }
  ];

  const breathingSteps = [
    {
      step: 1,
      title: '準備',
      description: '快適な姿勢で座り、目を閉じます',
      duration: '1分'
    },
    {
      step: 2,
      title: '吸気',
      description: '鼻からゆっくりと4秒かけて息を吸います',
      duration: '4秒'
    },
    {
      step: 3,
      title: '保持',
      description: '息を4秒間止めます',
      duration: '4秒'
    },
    {
      step: 4,
      title: '呼気',
      description: '口からゆっくりと6秒かけて息を吐きます',
      duration: '6秒'
    },
    {
      step: 5,
      title: '繰り返し',
      description: 'このサイクルを5-10回繰り返します',
      duration: '5-10分'
    }
  ];

  const meditationSteps = [
    {
      step: 1,
      title: '姿勢を整える',
      description: '背筋を伸ばして座り、手は膝の上に置きます',
      duration: '1分'
    },
    {
      step: 2,
      title: '呼吸に注意を向ける',
      description: '自然な呼吸に意識を集中します',
      duration: '2-3分'
    },
    {
      step: 3,
      title: '雑念を手放す',
      description: '浮かんだ思考を判断せずに手放します',
      duration: '継続'
    },
    {
      step: 4,
      title: '現在に戻る',
      description: '注意が逸れたら優しく呼吸に戻します',
      duration: '継続'
    },
    {
      step: 5,
      title: '終了',
      description: 'ゆっくりと目を開けて現実に戻ります',
      duration: '1分'
    }
  ];

  const muscleSteps = [
    {
      step: 1,
      title: '足の筋肉',
      description: '足の指から太ももまで5秒間緊張させ、10秒間リラックス',
      duration: '15秒'
    },
    {
      step: 2,
      title: '腹部・背中',
      description: 'お腹と背中の筋肉を緊張させ、リラックス',
      duration: '15秒'
    },
    {
      step: 3,
      title: '腕・肩',
      description: '腕と肩の筋肉を緊張させ、リラックス',
      duration: '15秒'
    },
    {
      step: 4,
      title: '首・顔',
      description: '首と顔の筋肉を緊張させ、リラックス',
      duration: '15秒'
    },
    {
      step: 5,
      title: '全身',
      description: '全身の力を抜いて深いリラックス状態を味わいます',
      duration: '2-3分'
    }
  ];

  const imagerySteps = [
    {
      step: 1,
      title: '場所を選ぶ',
      description: '心地よい場所（海辺、森、山など）をイメージします',
      duration: '1分'
    },
    {
      step: 2,
      title: '五感を使う',
      description: '見える景色、聞こえる音、感じる温度を想像します',
      duration: '2-3分'
    },
    {
      step: 3,
      title: '感情を味わう',
      description: 'その場所にいる時の平和な気持ちを感じます',
      duration: '2-3分'
    },
    {
      step: 4,
      title: '詳細を加える',
      description: 'より詳細な感覚や体験を追加します',
      duration: '2-3分'
    },
    {
      step: 5,
      title: '記憶に留める',
      description: 'この感覚を記憶し、必要な時に思い出せるようにします',
      duration: '1分'
    }
  ];

  const getStepsByTab = (tab: string) => {
    switch (tab) {
      case 'breathing': return breathingSteps;
      case 'meditation': return meditationSteps;
      case 'muscle': return muscleSteps;
      case 'imagery': return imagerySteps;
      default: return breathingSteps;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900 font-medium">リラクゼーション指導</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-50 to-teal-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/d623ca52151aa58cfbe2a15fd3a811e0.jpg"
            alt="リラクゼーション指導"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            リラクゼーション<span className="text-green-600">指導</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            呼吸法、瞑想、マインドフルネスなどの科学的に効果が実証されたリラクゼーション技法をご指導します
          </p>
        </div>
      </section>

      {/* Technique Selection */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {techniques.map((technique) => (
              <button
                key={technique.id}
                onClick={() => setActiveTab(technique.id)}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === technique.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-600'
                }`}
              >
                <i className={`${technique.icon} mr-2`}></i>
                {technique.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {techniques.find(t => t.id === activeTab)?.name}
            </h2>
            <p className="text-xl text-gray-600">
              {techniques.find(t => t.id === activeTab)?.description}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Steps */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">実践ステップ</h3>
              {getStepsByTab(activeTab).map((step, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h4>
                      <p className="text-gray-600 mb-2">{step.description}</p>
                      <span className="inline-block bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                        {step.duration}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Practice Area */}
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">ガイド付き実践</h3>
              
              <div className="bg-white rounded-lg p-6 mb-6">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${techniques.find(t => t.id === activeTab)?.icon} text-3xl text-green-600`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {techniques.find(t => t.id === activeTab)?.name}セッション
                  </h4>
                  <p className="text-gray-600">音声ガイドに従って実践してください</p>
                </div>

                <div className="text-center space-y-4">
                  <div className="text-2xl font-bold text-green-600">
                    {Math.floor(currentTimer / 60)}:{(currentTimer % 60).toString().padStart(2, '0')}
                  </div>
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className={`px-8 py-3 rounded-full font-semibold whitespace-nowrap cursor-pointer ${
                      isPlaying 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} mr-2`}></i>
                    {isPlaying ? '一時停止' : '開始'}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">効果と注意点</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>ストレスホルモンの減少</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>血圧・心拍数の安定</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                    <span>睡眠の質の向上</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
                    <span>無理をせず、自分のペースで行ってください</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Services */}
          <div className="mt-16 bg-gray-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">関連サービス</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/mental-health/stress-assessment" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-emotion-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">ストレス評価</h4>
                <p className="text-gray-600 text-sm">現在のストレス状態を評価</p>
              </Link>
              <Link to="/mental-health/sleep-improvement-support" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-moon-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">睡眠改善サポート</h4>
                <p className="text-gray-600 text-sm">良質な睡眠のための指導</p>
              </Link>
              <Link to="/mental-health/specialist-collaboration" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-links-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">専門医連携</h4>
                <p className="text-gray-600 text-sm">専門医との連携サポート</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">メンタルヘルス</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/mental-health/stress-assessment" className="hover:text-white cursor-pointer">ストレス評価</Link></li>
                <li><Link to="/mental-health/relaxation-guidance" className="hover:text-white cursor-pointer">リラクゼーション指導</Link></li>
                <li><Link to="/mental-health/sleep-improvement-support" className="hover:text-white cursor-pointer">睡眠改善サポート</Link></li>
                <li><Link to="/mental-health/specialist-collaboration" className="hover:text-white cursor-pointer">専門医連携</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康相談</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">サルコペニア予防</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">フレイル対策</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">栄養指導</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">ビースト君ブログ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
